import { useEffect, useState } from 'react';
import { ChevronRight, Plus, Trash2, UserPlus } from 'lucide-react';
import {
  PARTICIPANT_TYPE_OPTIONS,
  ROUTING_OPTIONS,
  RESPONSE_TYPE_OPTIONS,
  HIERARCHY_TYPE_OPTIONS,
  blankStage,
} from './bpmApprovalFlowShared';

const PARTICIPANT_BY_VALUE = Object.fromEntries(
  PARTICIPANT_TYPE_OPTIONS.map((o) => [o.value, o])
);

/**
 * Collapsible per-stage Fusion list-builder / auto-action detail
 * (number of levels lives on the hierarchy participant above).
 */
function StageAdvanced({ stage, onChange, disabled }) {
  const seeded = !!(
    stage.listBuilder ||
    stage.startingParticipant ||
    stage.topParticipant ||
    stage.autoAction ||
    stage.autoActionEnabled
  );
  const [open, setOpen] = useState(seeded);

  // Seeding (copy-to-editor) arrives just after mount; open the section once it has data.
  useEffect(() => {
    if (seeded) setOpen(true);
  }, [seeded]);

  const field = (label, key, placeholder) => (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <input
        type="text"
        value={stage[key] || ''}
        onChange={(e) => onChange({ [key]: e.target.value })}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-[11px]"
      />
    </label>
  );

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600"
      >
        <ChevronRight className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`} />
        List builder &amp; auto-action
      </button>
      {open ? (
        <div className="mt-1.5 grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-white p-2">
          {field('List builder', 'listBuilder', 'e.g. Supervisory')}
          {field('Starting participant', 'startingParticipant', 'HierarchyBuilder.getManager(…)')}
          {field('Top participant', 'topParticipant', 'HierarchyBuilder.getManager(…)')}
          <label className="inline-flex items-center gap-2 text-[11px] text-slate-600">
            <input
              type="checkbox"
              checked={stage.autoActionEnabled === true}
              onChange={(e) => onChange({ autoActionEnabled: e.target.checked })}
              disabled={disabled}
              className="h-3.5 w-3.5 rounded border-slate-300"
            />
            Auto action enabled
          </label>
          {field('Auto action', 'autoAction', 'e.g. "APPROVE"')}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Structured THEN editor: ordered approval stages, each with routing
 * (sequential/parallel), response type (Required/FYI), and one or more
 * participants (full Fusion participant type set).
 *
 * stages: Array<{ order, routing, responseType, approvers: [{ kind, ref }] }>
 */
export default function RuleStagesEditor({ stages = [], onChange, disabled = false }) {
  const list = stages.length ? stages : [blankStage(1)];

  const commit = (next) => {
    if (disabled) return;
    onChange(next.map((s, i) => ({ ...s, order: i + 1 })));
  };

  const updateStage = (si, patch) => commit(list.map((s, i) => (i === si ? { ...s, ...patch } : s)));

  const updateApprover = (si, ai, patch) =>
    commit(
      list.map((s, i) =>
        i === si
          ? { ...s, approvers: s.approvers.map((a, j) => (j === ai ? { ...a, ...patch } : a)) }
          : s
      )
    );

  const addApprover = (si) =>
    commit(
      list.map((s, i) =>
        i === si ? { ...s, approvers: [...(s.approvers || []), { kind: 'managementHierarchy', ref: '' }] } : s
      )
    );

  const removeApprover = (si, ai) =>
    commit(
      list.map((s, i) =>
        i === si
          ? {
              ...s,
              approvers:
                s.approvers.length > 1 ? s.approvers.filter((_, j) => j !== ai) : s.approvers,
            }
          : s
      )
    );

  const addStage = () => commit([...list, blankStage(list.length + 1)]);
  const removeStage = (si) => commit(list.length > 1 ? list.filter((_, i) => i !== si) : list);

  return (
    <div className="space-y-2">
      {list.map((stage, si) => (
        <div key={si} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
              Stage {si + 1}
            </span>
            <label className="inline-flex items-center gap-1 text-[11px] text-slate-600">
              Routing
              <select
                value={stage.routing || 'sequential'}
                onChange={(e) => updateStage(si, { routing: e.target.value })}
                disabled={disabled}
                className="rounded border border-slate-300 px-1.5 py-1 text-[11px]"
              >
                {ROUTING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="inline-flex items-center gap-1 text-[11px] text-slate-600">
              Response
              <select
                value={stage.responseType || 'required'}
                onChange={(e) => updateStage(si, { responseType: e.target.value })}
                disabled={disabled}
                className="rounded border border-slate-300 px-1.5 py-1 text-[11px]"
              >
                {RESPONSE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => removeStage(si)}
              disabled={disabled || list.length <= 1}
              title="Remove stage"
              className="ml-auto rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-1.5">
            {(stage.approvers || []).map((appr, ai) => {
              const meta = PARTICIPANT_BY_VALUE[appr.kind] || {};
              const onKindChange = (kind) => {
                const next = PARTICIPANT_BY_VALUE[kind] || {};
                const patch = { kind, ref: '' };
                if (next.isHierarchy) {
                  patch.levels = appr.levels != null ? appr.levels : 1;
                  patch.hierarchyType = appr.hierarchyType || 'LINE_MANAGER';
                } else {
                  patch.levels = undefined;
                  patch.hierarchyType = undefined;
                }
                updateApprover(si, ai, patch);
              };
              return (
                <div key={ai} className="rounded border border-slate-200 bg-white p-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={appr.kind}
                      onChange={(e) => onKindChange(e.target.value)}
                      disabled={disabled}
                      className="min-w-[12rem] flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                    >
                      {PARTICIPANT_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {meta.needsValue ? (
                      <input
                        type="text"
                        value={appr.ref || ''}
                        onChange={(e) => updateApprover(si, ai, { ref: e.target.value })}
                        placeholder={meta.valueLabel || 'Value'}
                        disabled={disabled}
                        className="min-w-[10rem] flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                      />
                    ) : null}
                    <button
                      type="button"
                      onClick={() => removeApprover(si, ai)}
                      disabled={disabled || stage.approvers.length <= 1}
                      title="Remove participant"
                      className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {meta.isHierarchy ? (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-1">
                      <label className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                        Levels
                        <input
                          type="number"
                          min={1}
                          value={appr.levels != null ? appr.levels : 1}
                          onChange={(e) =>
                            updateApprover(si, ai, { levels: Math.max(1, Number(e.target.value) || 1) })
                          }
                          disabled={disabled}
                          className="w-16 rounded border border-slate-300 px-1.5 py-1 text-[11px]"
                        />
                      </label>
                      <label className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                        Hierarchy type
                        <select
                          value={appr.hierarchyType || 'LINE_MANAGER'}
                          onChange={(e) => updateApprover(si, ai, { hierarchyType: e.target.value })}
                          disabled={disabled}
                          className="rounded border border-slate-300 px-1.5 py-1 text-[11px]"
                        >
                          {HIERARCHY_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  ) : null}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => addApprover(si)}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:border-cyan-400 hover:text-cyan-700 disabled:opacity-40"
            >
              <UserPlus className="h-3.5 w-3.5" /> Add participant
            </button>
          </div>

          <StageAdvanced
            stage={stage}
            onChange={(patch) => updateStage(si, patch)}
            disabled={disabled}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={addStage}
        disabled={disabled}
        className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-cyan-400 hover:text-cyan-700 disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" /> Add stage
      </button>
    </div>
  );
}
